import { useCallback, useState } from "react";
import {
  BoxIcon,
  CheckIcon,
  CrossIcon,
  DealTrackerIcon,
  DocumentIcon,
  PlayCircleIcon,
} from "../../Icons";
import { TradeFlowJson } from "../../../types/TradeFlow";
import { useGetTradeFlowByIdQuery } from "../../../store/apiSlices/tradeFlow";
import CustomModal from "../CustomModal";
import { FilePreview } from "./UploadDocVault";
import { useServeFileQuery } from "../../../store/apiSlices/common";
import { toast } from "react-toastify";

import ContainerStatusInfo from "./ContainerStatusInfo";
import { useSearchParams } from "react-router-dom";
import { useTradeFlowData } from "../../../context/TradeFlowDataContext ";
import { useTradeFlowTracker } from "../../../context/TradeFlowTrackerContext";
import { useDispatch } from "react-redux";
import { aiAutomateApi } from "../../../store/apiSlices/aiAutomate";

export const initialState = [
  {
    field: "Purchase Order Confirmed",
    complted: false,
  },
  {
    field: "Shipment Booked",
    complted: false,
  },
  {
    field: "QC Inspection",
    complted: false,
  },
  {
    field: "Shipment Pickup",
    complted: false,
  },
  {
    field: "Customs Clearance",
    complted: false,
  },
  {
    field: "Goods Delivered",
    complted: false,
  },
];

export const initaialTradeFlowData = initialState.map((el) => ({
  step: el,
  Contacts: [],
  document_vault: [],
}));

const STEP_WIDTH = 100;

export default function Summary() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocFile, setSelectedDocFile] = useState("");

  const [searchParams] = useSearchParams();

  const { isViewer, rowData } = useTradeFlowTracker();
  const { tradeFlowData, updateTradeFlow, openEditTab } = useTradeFlowData();

  const { trade_flow_json, id, row_id } = tradeFlowData;
  const { refetch } = useGetTradeFlowByIdQuery(row_id, {
    skip: !row_id,
  });

  const dispatch = useDispatch();

  const table_id = searchParams.get("table_id");

  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const getAllDocuments = useCallback(
    (steps: TradeFlowJson[]) => {
      return steps.flatMap((step) =>
        step.document_vault.filter(
          (doc) => doc.document_name && doc.document_name.trim(),
        ),
      );
    },
    [trade_flow_json],
  );

  const allDocuments = getAllDocuments(trade_flow_json).filter(
    (doc) => doc.document_file,
  );
  const totalSteps = trade_flow_json.length;

  const completedSteps = trade_flow_json.filter(
    (el) => el.step.complted,
  ).length;

  const progressPercent =
    totalSteps > 1
      ? (Math.max(0, completedSteps - 1) / (totalSteps - 1)) * 100
      : 0;

  const handleBtnClick = async (index: number) => {
    const updatedSteps = trade_flow_json.map((step, i) =>
      i === index
        ? {
            ...step,
            step: {
              ...step.step,
              complted: !step.step.complted,
            },
          }
        : step,
    );

    const recentCompletedStep = updatedSteps[index];

    const payload = {
      id,
      row_id,
      trade_flow_json: updatedSteps,
      table_id: Number(table_id),
      action: recentCompletedStep.step.complted
        ? recentCompletedStep.step.field
        : "",
    };

    await updateTradeFlow(payload);

    dispatch(aiAutomateApi.util.invalidateTags(["aiAutomateApi"]));
  };

  return (
    <section className="relative space-y-8 text-gray-900">
      <div className="flex items-center gap-10">
        <h3 className="w-[300px] text-sm flex items-center gap-1">
          <DealTrackerIcon />
          <span>Trade Flow Tracker</span>
        </h3>

        <div className="relative w-full overflow-auto">
          {/* Content-width wrapper */}
          <div
            className="relative flex items-start gap-3 text-xs"
            style={{ width: trade_flow_json.length * STEP_WIDTH }}
          >
            {/* Background line */}
            <div
              className="absolute top-[18px] h-[2px] bg-gray-300"
              style={{
                left: STEP_WIDTH / 2,
                right: STEP_WIDTH / 2,
              }}
            >
              {/* Progress line */}
              <div
                className="h-[2px] bg-gradient-to-r from-blue-800 to-blue-400 transition-all duration-300"
                style={{
                  width: `${progressPercent}%`,
                }}
              />
            </div>

            {/* Steps */}
            {trade_flow_json.map((el, idx) => (
              <div
                key={el.step_id}
                className="relative"
                style={{ width: STEP_WIDTH }}
              >
                <div className="h-[115px] text-center flex flex-col items-center gap-2">
                  <button
                    type="button"
                    className={`${
                      el.step.complted
                        ? "text-custom-blue-medium border-custom-blue-medium"
                        : "border-gray-900"
                    } p-1 bg-white border-2 z-10 rounded-full ${
                      isViewer
                        ? "cursor-not-allowed"
                        : "hover:scale-95 hover:text-custom-blue-medium hover:border-custom-blue-medium"
                    }`}
                    onClick={() => handleBtnClick(idx)}
                    disabled={isViewer}
                  >
                    {el.step.complted ? (
                      <CheckIcon className="size-6" />
                    ) : (
                      <CrossIcon className="size-6" />
                    )}
                  </button>
                  <p className="font-medium">{el.step.field}</p>

                  {!isViewer && el.step.field === "Shipment Booking" && (
                    <button
                      type="button"
                      className="p-[2px] text-custom-blue-medium rounded-lg hover:bg-gray-100 hover:scale-90"
                      onClick={openEditTab}
                    >
                      <PlayCircleIcon />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <h3 className="w-[260px] text-sm flex items-center gap-1">
          <DocumentIcon className="size-5" />
          <span>Documents</span>
        </h3>
        <ul className="flex flex-wrap gap-6 text-xs">
          {allDocuments.length > 0 ? (
            allDocuments.map((doc) => (
              <li
                key={doc.document_id}
                className="font-medium flex items-center gap-1 cursor-pointer hover:underline"
                onClick={() => {
                  refetch();

                  if (!doc.document_file) {
                    toast.warning("No file have been uploaded for a preview!");
                  }

                  setSelectedDocFile(doc.document_file!);
                  toggleModal();
                }}
              >
                <span className="block w-2 h-2 bg-gray-900" />
                <span>{doc.document_name}</span>
                {doc.document_file && (
                  <span className="font-bold text-custom-blue-medium">
                    &#8599;
                  </span>
                )}
              </li>
            ))
          ) : (
            <p className="text-xs text-gray-400">No uploaded document found</p>
          )}
        </ul>
      </div>

      <div className="flex items-center gap-2">
        <h3 className="w-[260px] text-sm flex items-center gap-1">
          <BoxIcon className="size-4" strokeWidth={0.4} />
          <span>Container Status</span>
        </h3>
        {/* <p className="w-full text-xs font-medium">
          Maersk V224: At Sea, ETA Dubai July 21,2025
        </p> */}
        <ContainerStatusInfo rowData={rowData} />
      </div>

      <CustomModal
        open={isModalOpen}
        onClose={toggleModal}
        className="rounded-xl p-6 max-w-6xl w-full"
      >
        <button
          onClick={toggleModal}
          className="text-white float-end hover:scale-95"
        >
          <CrossIcon className="size-6" />
        </button>

        <h2 className="text-white font-medium mb-4">File preview</h2>

        {selectedDocFile ? (
          <UploadedDocPreview document_file={selectedDocFile} />
        ) : (
          <p className="text-center text-gray-400 text-sm">No file found</p>
        )}
      </CustomModal>
    </section>
  );
}

function UploadedDocPreview({ document_file }: { document_file: string }) {
  const { data: uploadedDocFile, isLoading: isLoadingDocFile } =
    useServeFileQuery(document_file!, {
      skip: !document_file,
    });

  return (
    <div className="h-[80vh]">
      <FilePreview
        isLoadingDocFile={isLoadingDocFile}
        uploadedDocFile={uploadedDocFile}
      />
    </div>
  );
}
